import React, { FC } from 'react';
import { Col, Collapse, Divider, Empty, Row, Space } from 'antd';
import { RouteComponentProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import styled from 'styled-components';
import { isEmpty } from 'lodash';

import { PageHeader } from '~/layout';
import { usePermissions } from '~/hooks';
import { CountryField, NoPermission, TextField } from '~/components';
import Filters from './Filters';
import { Beneficiary } from '~/types';
import BeneficiaryCreate from './BeneficiaryCreate';
import buildAddress from '~/utils/buildAddress';
import { PersonIcon, BusinessIcon } from '~/img';
import AddAccount from './AddAccount';
import AccountList from './AccountList';
import BeneficiaryActions from './BeneficiaryActions';
import { useBeneficiariesQuery } from '~/hooks/beneficiaries';
import { useKycCheck } from '~/hooks/kyc';
import KycContent from '../kyc/KycContent';
import { getBeneficiaryName } from '~/utils/beneficiaries';

interface Props extends RouteComponentProps {
  className?: string;
}

const BeneficiariesPage: FC<Props> = (props) => {
  const { className } = props;
  const [t] = useTranslation('payouts');
  const dt = (key: string): string => t(`translation:${key}`);

  const { data, isFetching } = useBeneficiariesQuery();
  const kycCheck = useKycCheck();
  const hasAccessView = usePermissions('business.beneficiary.view');
  const hasAccessUpdate = usePermissions('business.beneficiary.update');
  if (!hasAccessView) return <NoPermission />;
  if (!kycCheck.isCompleted) return <KycContent {...kycCheck} />;
  return (
    <div className={cx(className, 'PayoutsPage')}>
      <Row gutter={gutter}>
        <Col span={24}>
          <PageHeader
            heading={t('beneficiaries')}
            showSearch={false}
            searchPlaceholder={t('searchPlaceholder')}
            isFetching={isFetching}
          />
        </Col>
      </Row>
      <Space direction="vertical" size={24} style={fullWidth}>
        <Row gutter={gutter} justify="space-between" align="middle">
          <Col>
            <Filters />
          </Col>
          <Col>
            <BeneficiaryCreate />
          </Col>
        </Row>
        {!isEmpty(data) ? (
          <Collapse className="PayoutsCollapse">
            {data?.map((item) => {
              const { type, Number, number } = item.document || {};
              const docNum = Number || number;
              return (
                <Collapse.Panel
                  key={item.id}
                  header={<Header data={item} />}
                  extra={<BeneficiaryActions beneficiary={item} />}
                >
                  <Row gutter={[16, 16]}>
                    {item.country && (
                      <Col span={12}>
                        <TextField label={dt('country')}>
                          <CountryField country={item.country} />
                        </TextField>
                      </Col>
                    )}
                    {item.phoneNumber && (
                      <Col span={12}>
                        <TextField label={dt('phone')}>
                          <bdi>{item.phoneNumber}</bdi>
                        </TextField>
                      </Col>
                    )}
                    {type && (
                      <Col span={12}>
                        <TextField label={t('primaryDocument')}>
                          {type && `${type} ${docNum ? `#${docNum}` : ''}`}
                        </TextField>
                      </Col>
                    )}
                    {item.email && (
                      <Col span={12}>
                        <TextField label={dt('email')}>{item.email}</TextField>
                      </Col>
                    )}
                    {item.address.postalCode && (
                      <Col span={12}>
                        <TextField label={dt('address')}>{buildAddress(item.address)}</TextField>
                      </Col>
                    )}
                  </Row>
                  {hasAccessUpdate ? (
                    <Divider>
                      <AddAccount beneficiary={item} />
                    </Divider>
                  ) : (
                    <Divider />
                  )}
                  <AccountList beneficiary={item} />
                </Collapse.Panel>
              );
            })}
          </Collapse>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Space>
    </div>
  );
};

interface HeaderProps {
  data: Beneficiary;
}

const Header: FC<HeaderProps> = (props) => {
  const { data } = props;
  const { type } = data;
  const getIcon = () => {
    switch (type) {
      case 'INDIVIDUAL':
        return <PersonIcon />;
      case 'BUSINESS':
        return <BusinessIcon />;
      default:
        return null;
    }
  };
  return (
    <Space className="PayoutsCollapse-Header" size={8}>
      <div>{getBeneficiaryName(data)}</div>
      <div className="text-gray">{getIcon()}</div>
    </Space>
  );
};

const gutter: [number, number] = [16, 24];
const fullWidth = { width: '100%' };

export default styled(BeneficiariesPage)`
  padding-bottom: 24px;
  .ant-collapse-content-box {
    padding-left: 40px;
    .ant-row {
      margin-bottom: 0 !important;
    }
  }
  .PayoutsCollapse-Header {
    font-size: 18px;
  }
  .PayoutsCollapse-OptionsButton {
    margin: -2px 0;
  }
`;
